import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Bell,
  CheckCheck,
  Clock,
  ExternalLink,
  Info,
  Loader2,
  ShieldAlert,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { cn } from '../lib/utils'
import {
  NotificationStatus,
  NotificationType,
  UserNotification,
  notificationService,
} from '../services/notification.service'

const typeStyles: Record<NotificationType, string> = {
  CRITICA: 'bg-red-100 text-red-700',
  INFORMATIVA: 'bg-blue-100 text-blue-700',
}

const typeLabels: Record<NotificationType, string> = {
  CRITICA: 'Critica',
  INFORMATIVA: 'Informativa',
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('es-MX', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value))
}

function redirectPathFor(notification: UserNotification) {
  if (!notification.is_redirectable) return null
  return notification.details?.redirect_path ?? null
}

export function Notifications() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<NotificationStatus>('all')
  const [type, setType] = useState<'all' | NotificationType>('all')

  const filters = {
    status: status === 'all' ? undefined : status,
    type: type === 'all' ? undefined : type,
    per_page: 30,
  }

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['notifications', filters],
    queryFn: () => notificationService.getNotifications(filters),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
  })

  const invalidateNotifications = () => {
    queryClient.invalidateQueries({ queryKey: ['notifications'] })
  }

  const markOneMutation = useMutation({
    mutationFn: (id: number) => notificationService.markAsViewed(id),
    onSuccess: invalidateNotifications,
    onError: () => toast.error('No se pudo marcar la notificacion'),
  })

  const markAllMutation = useMutation({
    mutationFn: () => notificationService.markAllAsViewed(),
    onSuccess: () => {
      invalidateNotifications()
      toast.success('Notificaciones marcadas como vistas')
    },
    onError: () => toast.error('No se pudieron actualizar las notificaciones'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => notificationService.deleteNotification(id),
    onSuccess: invalidateNotifications,
    onError: () => toast.error('No se pudo eliminar la notificacion'),
  })

  const notifications = data?.data ?? []
  const unreadCount = notifications.filter((notification) => !notification.viewed_at).length

  const handleOpen = async (notification: UserNotification) => {
    if (!notification.viewed_at) {
      await markOneMutation.mutateAsync(notification.id)
    }

    const redirectPath = redirectPathFor(notification)
    if (redirectPath) {
      navigate(redirectPath)
    }
  }

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Notificaciones</h1>
          <p className="mt-1 text-sm text-slate-600">
            {data?.total ?? 0} registros, {unreadCount} sin leer en esta vista
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={status} onValueChange={(value) => setStatus(value as NotificationStatus)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="unread">Sin leer</SelectItem>
              <SelectItem value="read">Vistas</SelectItem>
            </SelectContent>
          </Select>

          <Select value={type} onValueChange={(value) => setType(value as 'all' | NotificationType)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              <SelectItem value="CRITICA">Criticas</SelectItem>
              <SelectItem value="INFORMATIVA">Informativas</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            onClick={() => markAllMutation.mutate()}
            disabled={markAllMutation.isPending}
          >
            {markAllMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCheck className="h-4 w-4" />
            )}
            Marcar vistas
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <Bell className="h-4 w-4" />
            Bandeja
          </div>
          {isFetching && !isLoading && (
            <span className="text-xs text-slate-500">Actualizando...</span>
          )}
        </div>

        {isLoading ? (
          <div className="flex h-48 items-center justify-center text-sm text-slate-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Cargando notificaciones
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex h-56 flex-col items-center justify-center px-4 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
              <Bell className="h-6 w-6 text-slate-400" />
            </div>
            <p className="font-medium text-slate-900">Sin notificaciones</p>
            <p className="mt-1 text-sm text-slate-500">
              Cuando algun modulo genere avisos, apareceran aqui.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {notifications.map((notification) => {
              const redirectPath = redirectPathFor(notification)
              const isUnread = !notification.viewed_at
              const Icon = notification.type === 'CRITICA' ? ShieldAlert : Info

              return (
                <div
                  key={notification.id}
                  className={cn(
                    'grid gap-3 px-4 py-4 transition-colors md:grid-cols-[1fr_auto]',
                    isUnread ? 'bg-green-50/40' : 'bg-white',
                    redirectPath && 'cursor-pointer hover:bg-slate-50',
                  )}
                  onClick={() => handleOpen(notification)}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100">
                        <Icon className="h-4 w-4 text-slate-600" />
                      </span>
                      <h2 className="text-sm font-semibold text-slate-900">
                        {notification.title}
                      </h2>
                      <Badge className={typeStyles[notification.type]}>
                        {typeLabels[notification.type]}
                      </Badge>
                      {isUnread && (
                        <Badge className="bg-green-100 text-green-700">
                          Nueva
                        </Badge>
                      )}
                    </div>

                    {notification.description && (
                      <p className="mt-2 text-sm leading-6 text-slate-600">
                        {notification.description}
                      </p>
                    )}

                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {formatDate(notification.created_at)}
                      </span>
                      {notification.module && <span>Modulo: {notification.module}</span>}
                      {redirectPath && (
                        <span className="inline-flex items-center gap-1 text-green-700">
                          <ExternalLink className="h-3.5 w-3.5" />
                          Abrir modulo
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 md:justify-end">
                    {isUnread && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(event) => {
                          event.stopPropagation()
                          markOneMutation.mutate(notification.id)
                        }}
                      >
                        <CheckCheck className="h-4 w-4" />
                        Vista
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(event) => {
                        event.stopPropagation()
                        deleteMutation.mutate(notification.id)
                      }}
                      aria-label="Eliminar notificacion"
                    >
                      <Trash2 className="h-4 w-4 text-slate-500" />
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
